import { Tooltip } from "@mui/material";
import styled from "styled-components";
import SectionWrapper from "utilities/SectionWrapper";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useState, useMemo, useEffect } from "react";
import AppContext from "AppContext";
import { useSidebar } from "contexts/SidebarContext";
import Waves from "assets/wave.png";
import { useProfileState } from "hooks/useProfileState";
import { useUserState } from "hooks/useUserState";
import Tools from "views/tools/Tools";
import { NavItems, Orgs } from "website/navutilities";

import Priorities from "pages/dashboard/priorities/Priorities";
import WelcomeBox from "pages/dashboard/WelcomeBox";

export default function Dashboard({ activeTab, tabContent }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { isCollapsed } = useSidebar();
  const { permissions, userData, organizations } = useUserState();
  const { platformUser } = useContext(AppContext);
  const [userRole, setUserRole] = useState('');
  const [userOrganization, setUserOrganization] = useState(null);

  const tabs = useMemo(() => {
    const allTabs = [];

    // Only show admin tab if user has admin organizations
    if (organizations?.admin?.length > 0) {
      allTabs.push({ id: "admin", label: "🔐 Admin Console", route: "/dashboard/admin", status: "enabled" });
    }

    // Only show dashboard if user has any active organizations
    if (permissions?.isActive) {
      allTabs.push({ id: "default", label: "🎯 Dashboard", route: "/dashboard", status: "enabled" });
    }

    // Set user role from profile data
    const userRole = userData?.profiledata?.userRoles || '';
    setUserRole(userRole);

    // Add remaining tabs
    allTabs.push(
      { id: "profile", label: "👩🏽‍💻 Profile", route: "/dashboard/profile", status: "enabled" },
      { id: "index", label: "⚡️ Index", status: "disabled" }
    );

    return allTabs;
  }, [organizations, permissions, userData]);

  const navigate = useNavigate();

  const handleTabClick = (tab) => {
    navigate(tab.route);
  };

  // Find matching NavItem based on user role
  const matchingNavItem = useMemo(() => {
    return NavItems.find(item => item.role === userRole) || NavItems[0]; // fallback to first item if no match
  }, [userRole]);

  // Find matching OrgItem based on organization from userState
  const matchingOrgItem = useMemo(() => {
    // Log organizations data for debugging
    console.log('Organizations from userState:', organizations);
    console.log('Available Orgs:', Orgs.map(org => ({ id: org.id, route: org.route })));
    
    // Check if user has any organizations
    if (organizations?.admin?.length > 0) {
      // Log the first admin organization to see its structure
      console.log('First admin organization:', organizations.admin[0]);
      
      // Try different properties that might contain the organization identifier
      const adminOrg = organizations.admin[0];
      let orgId = adminOrg.organization_id;
      
      // If organization_id is undefined, try other properties
      if (!orgId && adminOrg.organizations) {
        orgId = adminOrg.organizations.route || adminOrg.organizations.id;
      }
      
      // If still no orgId, try the organization name or other properties
      if (!orgId && adminOrg.name) {
        orgId = adminOrg.name;
      }
      
      console.log('Admin orgId to match (after fallbacks):', orgId);
      
      // Find matching org in Orgs array if orgId exists
      if (orgId) {
        // Try matching by route first
        let matchingOrg = Orgs.find(
          (item) => item.route.toLowerCase() === orgId.toLowerCase()
        );
        
        // If no match by route, try matching by name
        if (!matchingOrg) {
          matchingOrg = Orgs.find(
            (item) => item.name.toLowerCase().includes(orgId.toLowerCase())
          );
        }
        
        if (matchingOrg) {
          console.log('Found matching org:', matchingOrg.route);
          return matchingOrg;
        } else {
          console.log('No matching org found for admin orgId:', orgId);
        }
      }
    } else if (organizations?.member?.length > 0) {
      // Log the first member organization to see its structure
      console.log('First member organization:', organizations.member[0]);
      
      // Try different properties that might contain the organization identifier
      const memberOrg = organizations.member[0];
      let orgId = memberOrg.organization_id;
      
      // If organization_id is undefined, try other properties
      if (!orgId && memberOrg.organizations) {
        orgId = memberOrg.organizations.route || memberOrg.organizations.id;
      }
      
      // If still no orgId, try the organization name or other properties
      if (!orgId && memberOrg.name) {
        orgId = memberOrg.name;
      }
      
      console.log('Member orgId to match (after fallbacks):', orgId);
      
      // Find matching org in Orgs array if orgId exists
      if (orgId) {
        // Try matching by route first
        let matchingOrg = Orgs.find(
          (item) => item.route.toLowerCase() === orgId.toLowerCase()
        );
        
        // If no match by route, try matching by name
        if (!matchingOrg) {
          matchingOrg = Orgs.find(
            (item) => item.name.toLowerCase().includes(orgId.toLowerCase())
          );
        }
        
        if (matchingOrg) {
          console.log('Found matching org:', matchingOrg.route);
          return matchingOrg;
        } else {
          console.log('No matching org found for member orgId:', orgId);
        }
      }
    }
    
    // Log that no matching organization was found
    console.log('No matching organization found, using default');
    return Orgs[0]; // fallback to first item if no organizations or no orgId
  }, [organizations]);

  // Log the matchingOrgItem's image whenever it changes
  useEffect(() => {
    if (matchingOrgItem) {
      console.log('Matching Org Image:', matchingOrgItem.image);
      console.log('Matching Org Background Image:', matchingOrgItem.backgroundImage);
      console.log('Matching Org:', matchingOrgItem);
    }
  }, [matchingOrgItem]);

  return (
    <SectionWrapper>
      <Dash className="SuperApp">
        <LeftSide className="LeftSide" $isCollapsed={isCollapsed}>
          <LeftSideContent className="LeftSideContent">
            {!isCollapsed && (
              <>
                <WelcomeBox />
                <Priorities />
                <BackgroundImage>
                  <img 
                    src={matchingOrgItem?.backgroundImage || matchingNavItem.backgroundImage} 
                    alt={(matchingOrgItem?.name || matchingNavItem.role || 'Background')} 
                  />
                </BackgroundImage>
              </>
            )}
          </LeftSideContent>
        </LeftSide>
        <RightSide className="RightSide" $isCollapsed={isCollapsed}>
          <DashboardContent>
            <WallMenu>
              {tabs.map((tab) => (
                <Tooltip
                  key={tab.id}
                  title={tab.status === "disabled" ? "Complete Season 0 to unlock." : ""}
                  arrow
                  placement="right"
                  $sx={{
                    tooltip: {
                      backgroundColor: '#000',
                      fontSize: '11px',
                      padding: '8px 12px'
                    }
                  }}
                >
                  <MenuItem
                    className={`MenuItem ${activeTab === tab.id ? "active" : ""} ${tab.status !== "enabled" ? "disabled" : ""}`}
                    onClick={() => tab.status === "enabled" && handleTabClick(tab)}
                  >
                    {tab.label}
                  </MenuItem>
                </Tooltip>
              ))}
            </WallMenu>
            <WallContent>{tabContent}</WallContent>
          </DashboardContent>
        </RightSide>
      </Dash>
      {permissions?.isActive ? (
        <Tools />
      ) : (
        <div style={{ height: '80px', width: '100%', background: 'black', position: 'absolute', bottom: 0, left: 0 }} />
      )}
    </SectionWrapper>
  );
}

const Dash = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row 
  background: white;
  width: calc(100vw - 65px);
  height: calc(100vh - 75px);
  overflow-x: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
    // width: calc(100vw - 80px);
  }
`;

const LeftSide = styled.div`
  display: flex;
  box-sizing: border-box;
  background: black;
  flex-direction: column;
  transition: all 0.3s ease;
  padding: ${props => props.$isCollapsed ? '12px 10px 15px' : '12px 20px 15px'};
  gap: 15px;
  z-index: 2;
  width: ${props => props.$isCollapsed ? 'auto' : '285px'};
  min-width: ${props => props.$isCollapsed ? 'auto' : '285px'};
  flex: ${props => props.$isCollapsed ? '0' : '1'};
  height: calc(100vh - 75px);
  overflow: ${props => props.$isCollapsed ? 'hidden' : 'visible'};
  @media screen and (max-width: 768px) {
    width: 100%;
    .ProfileCard {
      max-width: 100%;
    }
  }
`;

const LeftSideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  border-radius: 3px;
  height: fill-available;
  flex: 1;
  @media screen and (max-height: 768px) {
    // overflow-y: scroll;
    // margin-right: -5px;
  }
  @media screen and (max-width: 768px) {
    // display: none;
  }
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: calc(100vh - 75px);
  width: ${props => props.$isCollapsed ? 'calc(100vw - 85px)' : 'calc(100vw - 355px)'};
  background: #f7f7f7;
  transition: width 0.3s ease;
  @media screen and (max-width: 768px) {
    height: calc(100vh - 200px);
    width: calc(100vw - 65px);
  }
  @media screen and (max-width: 640px) {
    // height: calc(100vh - 315px);
  }
`;

const WallMenu = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  // align-self: center;
  box-sizing: border-box;
  gap: 10px;
  align-items: center;
  padding: 20px 0px 20px 0px;
  margin-left: 40px;
  a {
    text-decoration: none;
    color: initial;
    &:hover {
      cursor: pointer;
    }
  }
  @media screen and (max-width: 1165px) {
    align-self: initial;
    // overflow-x: scroll;
    overflow-y: hidden;
    padding-top: 15px;
  }
  @media screen and (max-width: 640px) {
    // margin-bottom: 0px;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 18px 5px 15px;
  height: 30px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  gap: 6px;
  transition: all, 0.3s;
  background: #aecad23d;
  text-decoration: none;
  &:hover,
  &.active {
    cursor: pointer;
    background: black;
    color: white;
  }
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: #aecad23d;
      color: inherit;
    }
  }
`;

const WallContent = styled.div`
  height: calc(100% - 90px);
  box-shadow: 0px 0px 5px 1px #f1f1f1;
  overflow: hidden;
  border-radius: 5px;
  background: white;
  border: 1px solid #f8f8f8;
  display: flex;
  flex-wrap: wrap;
  box-sizing: border-box;
  gap: 15px;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 10px;
  margin: 0px 10px;
  position: relative;
  flex-direction: column;
  .ButtonContainer {
    position: absolute;
    bottom: 0px !important;
  }
  .ProfileSignup {
    padding-bottom: 90px;
  }
`;

const DashboardContent = styled.div`
  height: calc(100%);
  &.hide {
    display: none;
  }
`;

const BackgroundImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 5px;
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 4px;
  }
`;